import { FC, ReactNode, useCallback, useEffect, useState } from "react";
import { TransactionsFilters } from "../../transactions/filters/lib";
import { CampaignCardProps } from "../ActionItem";
import { TransactionsUpdateMetaDto } from "../../../common/dto/actions/transactions-update-meta.dto";
import { WithRequiredProperties } from "../../../common/types/base/generics";
import { TransactionSortValue, useSort } from "../../transactions/useSort";
import { useTransactionsQuery } from "../../transactions/useTransactionsQuery";
import { ActionCardCompleted } from "../ActionCardCompleted";
import { ActionCloseButton } from "../ActionCloseButton";
import { ActionCardSection } from "../ActionCardSection";
import { ActionStatus } from "../../../common/types/actions";
import { Button } from "../../general/Button/Button";
import { ActionConfirmButton } from "../ActionConfirmButton";
import { ActionCard } from "../ActionCard";
import { ActionTransactionViewModal } from "../ActionTransactionViewModal";
import { TransactionView } from "../../../common/types/views/transactionViews";
import styles from "./TransactionsUpdateActionCard.module.scss";
import { TransactionsSection } from "./TransactionsSection";

interface TransactionsUpdateActionCardProps
    extends CampaignCardProps<TransactionsUpdateMetaDto> {
    transactionFilters: WithRequiredProperties<
        TransactionsFilters,
        "entityIds"
    >;
    icon: ReactNode;
    completedActionHeader: string;
    pendingActionHeader: ReactNode;
    getTransactionsSectionTitle(transactionsCount: number): ReactNode;
    view: TransactionView;
    transactionsModalTitle?: string;
}

export const TransactionsUpdateActionCard: FC<
    TransactionsUpdateActionCardProps
> = ({
    action,
    onConfirm,
    onSkip,
    isLoading,
    icon,
    completedActionHeader,
    pendingActionHeader,
    transactionFilters,
    getTransactionsSectionTitle,
    view,
    transactionsModalTitle = "All transactions",
    ...rest
}) => {
    const [showAllTransactions, setShowAllTransactions] = useState(false);
    const { isCompleted, opened } = rest;
    const { sortExpression } = useSort(TransactionSortValue.DATE_DESC);
    const [confirmed, setConfirmed] = useState(false);

    const {
        query: {
            data: transactionsResponse,
            isLoading: isLoadingTransactions,
            isSuccess,
            refetch,
        },
    } = useTransactionsQuery(
        {
            sort: sortExpression,
            filters: transactionFilters,
            page: 1,
            limit: 5,
        },
        { enabled: opened, refetchOnWindowFocus: false },
    );

    const handleViewAll = useCallback(() => {
        setShowAllTransactions(true);
    }, []);

    const confirmAction = useCallback(() => {
        setConfirmed(true);
        onConfirm?.({
            ...action,
            meta: {
                updatedTransactions: [], // no tracking this until transaction views
                skippedTransactions: [],
            },
        });
    }, [action, onConfirm]);

    const skipAction = useCallback(() => {
        onSkip?.({
            ...action,
            meta: {
                ...action.meta,
                skippedTransactions: [
                    ...(transactionsResponse?.data ?? []).map(
                        (transaction) => transaction.id,
                    ),
                ],
                uploadedReceipts: [],
            },
        });
    }, [action, onSkip, transactionsResponse?.data]);

    useEffect(() => {
        if (
            !isLoadingTransactions &&
            isSuccess &&
            transactionsResponse?.total === 0 &&
            !confirmed
        ) {
            // If there are no transactions, automatically confirm the action
            confirmAction();
        }
    }, [
        isLoadingTransactions,
        isSuccess,
        transactionsResponse,
        confirmAction,
        isCompleted,
        confirmed,
    ]);

    const firstSection = (inOpened: boolean, closeCard: () => void) =>
        isCompleted ? (
            <ActionCardCompleted header={completedActionHeader}>
                <ActionCloseButton onClick={closeCard} />
            </ActionCardCompleted>
        ) : (
            <ActionCardSection
                entity={action.entity}
                inOpened={inOpened}
                header={pendingActionHeader}
                icon={icon}
                ctas={
                    <>
                        {action.status !== ActionStatus.SKIPPED && (
                            <Button
                                variant="secondary"
                                onClick={() => skipAction()}
                            >
                                Skip
                            </Button>
                        )}
                        <ActionConfirmButton
                            loading={isLoading}
                            onClick={confirmAction}
                            disabled={
                                isLoadingTransactions ||
                                transactionsResponse?.total !== 0
                            }
                        />
                    </>
                }
                isTitle
            />
        );

    const otherSections = (inOpened: boolean) => (
        <TransactionsSection
            isLoading={isLoadingTransactions}
            data={transactionsResponse}
            inOpened={inOpened}
            onViewAll={handleViewAll}
            sectionTitle={getTransactionsSectionTitle}
            className={styles.transactionsSection}
        >
            {(transactions) => (
                <ActionTransactionViewModal
                    title={transactionsModalTitle}
                    size="xl"
                    view={view}
                    show={transactions.length > 0 && showAllTransactions}
                    onHide={() => {
                        setShowAllTransactions(false);
                        refetch();
                    }}
                />
            )}
        </TransactionsSection>
    );

    return (
        <ActionCard
            {...rest}
            firstSection={firstSection}
            otherSections={otherSections}
        />
    );
};
